import { Button } from "@rosc/ui/v32/button";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { RotateCw, TriangleAlert } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { V3Logo } from "@/components/ui/v3/logo";

// The address in ROSC's account emails (api email layout).
export const SUPPORT_EMAIL = "support@rosc.ai";

// The router's error page (defaultErrorComponent): a screen crashed. The
// logo, a short message, Reload, Go to visits and the support address, on
// v3.2 tokens in both themes. Plain links, so it works when the router
// itself is what failed. The error stays out of the page (it may hold
// visit content); it is logged to the console for support.
export const AppErrorPage = ({
  error,
}: Partial<ErrorComponentProps>): JSX.Element => {
  const { t } = useTranslation();
  if (error) console.error(error);
  return (
    <div
      className="flex min-h-screen flex-col items-center bg-v3-canvas px-4 font-v3 text-v3-text supports-[min-height:100dvh]:min-h-dvh"
      data-testid="app-error-page"
    >
      <header className="flex h-16 shrink-0 items-center pt-[env(safe-area-inset-top)]">
        <V3Logo size={28} />
      </header>
      <main className="flex w-full flex-1 items-center justify-center pb-16">
        <div
          className="w-full max-w-md rounded-[18px] bg-v3-panel p-6 text-center shadow-v3-hairline sm:p-8"
          role="alert"
        >
          <span
            aria-hidden
            className="mx-auto flex size-12 items-center justify-center rounded-[14px] bg-v3-rec-tint text-v3-rec-text"
          >
            <TriangleAlert className="size-6" />
          </span>
          <h1 className="mt-4 text-[20px] font-semibold leading-7 tracking-tight">
            {t("appError.title")}
          </h1>
          <p className="mt-1.5 text-[14px] leading-relaxed text-v3-text-2">
            {t("appError.description")}
          </p>
          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button
              data-testid="app-error-reload"
              variant="primary"
              onClick={() => window.location.reload()}
            >
              <RotateCw aria-hidden />
              {t("appError.reload")}
            </Button>
            <Button asChild variant="secondary">
              <a data-testid="app-error-visits" href="/visits">
                {t("appError.goToVisits")}
              </a>
            </Button>
          </div>
          <p className="mt-5 text-[13px] text-v3-text-3">
            {t("appError.support")}{" "}
            <a
              className="font-medium text-v3-text underline-offset-4 hover:underline"
              href={`mailto:${SUPPORT_EMAIL}`}
            >
              {SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};
